// R55 — CONTINUE WATCHING, THE PANEL. Props in, tree out, no hook, so a gate CALLS it.
//
// **THE THING THAT MOVED IS THE THING THAT NEEDED AN INSTRUMENT** (roadmap 7's own currency). Three
// laws were written inline inside a thirty-hook component and held by nothing at all:
//
//   1. **T8-9's DISTINCTION, one panel over.** `videos.empty` says *"Start a class in the Video
//      Library and it will wait for you here"* — a statement of fact about her account. A read that
//      FAILED has no way of knowing that, and until R55 it printed that sentence anyway. Which of
//      the two sentences a member reads is now a function of `load`, and a fixture asks it.
//   2. **THE PERCENTAGE.** A video whose metadata never loaded has a position and NO duration, so
//      the bar's own arithmetic has to survive a null denominator — and the bar must not be drawn
//      for a finished video, whose position equals its length and would otherwise read 100% under
//      the word "Finished".
//   3. **THE RESUME POINT.** A finished video restarts at 0 and an unfinished one picks up where she
//      left off. That is one ternary, buried in an `onClick`, and it is the whole feature.
//
// The remove button's accessible name carries the TITLE (14-B5's lesson: an accessible name is the
// one kind of copy nobody looking at the screen can see is wrong), and the note sits at PANEL width
// above the list rather than inside a row's actions — R35's capture, where a confirm rendered inside
// a 190px chip was clipped to nonsense and the member tapped the only button she could see.
import { Trash2 } from "lucide-react";
import { t } from "../strings.js";
import type { WatchedVideo } from "../lib/video-progress";

// The percentage law, EXPORTED so a gate asks it directly rather than reading it off a style
// attribute. A finished video has no bar, and a video with no known length has no percentage.
export function watchedPercent(video: { positionSeconds: number; durationSeconds: number | null }): number {
  if (!video || !video.durationSeconds) return 0;
  return Math.min(100, Math.round((video.positionSeconds / video.durationSeconds) * 100));
}

// The resume point. A finished video starts over; an unfinished one carries on.
export function resumeAt(video: { positionSeconds: number; completed: boolean }): number {
  return video.completed ? 0 : video.positionSeconds;
}

export function WatchList({ videos, language, load, note, onResume, onRemove }: {
  videos: WatchedVideo[];
  language: string;
  // T8-9's three states. "It arrived empty" and "it did not arrive" are different sentences.
  load: "loading" | "ok" | "error";
  note: string;
  onResume: (video: WatchedVideo) => void;
  onRemove: (slug: string) => void;
}) {
  return (
    <>
      {note ? <p className="room-panel__note" role="status">{note}</p> : null}
      {videos.length ? (
        <ul className="watch-list">
          {videos.map((v) => {
            const pct = watchedPercent(v);
            return (
              <li key={v.slug} className="watch-list__item">
                <div className="watch-list__who">
                  <strong>{v.title}</strong>
                  <span className="watch-list__meta">
                    {v.completed ? t(language, "videos.finished") : pct > 0 ? t(language, "videos.watched", { percent: pct }) : t(language, "videos.notStarted")}
                  </span>
                  {pct > 0 && !v.completed ? <span className="watch-list__bar"><span className="watch-list__bar-fill" style={{ width: `${pct}%` }} /></span> : null}
                </div>
                <div className="watch-list__actions">
                  <button type="button" className="button button--ghost" onClick={() => onResume(v)}>
                    {t(language, v.completed ? "videos.again" : "videos.resume")}
                  </button>
                  <button type="button" className="watch-list__delete" onClick={() => onRemove(v.slug)} aria-label={t(language, "videos.remove", { title: v.title })}>
                    <Trash2 size={15} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="room-panel__empty">{t(language, load === "error" ? "videos.loadFailed" : "videos.empty")}</p>
      )}
    </>
  );
}
